import { twMerge } from "tailwind-merge";
import { Card, CardContent, CardHeader, H5 } from "@sellar-io/components";
import type {
  OrderCommentListResponseType,
  PaymentLinkOrderCommentListResponseType,
} from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { ReactElement, useState } from "react";
import { SpinnerLegacy } from "../../index";
import { useOrderCommentCreate } from "../../../hooks/api/v2/orderComment/useOrderCommentCreate";
import { Comment } from "../../comments/Comment";
import { CommentDialog } from "../../comments/CommentDialog";
import { EnablePushNotificationAlert } from "../../pushNotification/EnablePushNotificationAlert";

export const OrderCommentListContent = (props: {
  clamp?: number;
  limit?: number;
  orderId: number;
  connection?: {
    connectionType: "prospect" | "customer";
  };
  query: UseInfiniteQueryResult<
    InfiniteData<
      OrderCommentListResponseType | PaymentLinkOrderCommentListResponseType
    >,
    Error
  >;
}): ReactElement => {
  const mutation = useOrderCommentCreate();
  const [createCommentActive, setCreateCommentActive] = useState(false);

  const query = props.query;

  if (query.isLoading) return <SpinnerLegacy />;

  if (!query.data) return <div>Oops</div>;

  return (
    <>
      <Card className="bg-white">
        <CardHeader
          className={twMerge(
            "bg-white",
            "flex items-center justify-between p-4",
          )}
        >
          <H5>Comments ({query.data.pages[0]?.length})</H5>
        </CardHeader>
        {!!query.data.pages[0]?.length && (
          <CardContent className={"flex flex-col gap-4 p-4"}>
            <EnablePushNotificationAlert
              title="Enable order comment notifications?"
              message="Receive notifications for any order comments and updates in your browser."
              className="w-full"
              showSettings={true}
            />
            {query.data.pages[0].map((comment, i) => {
              if (!props.limit || i < props.limit) {
                return (
                  <Comment
                    key={i}
                    comment={{
                      id: comment.id,
                      createdAt: comment.createdAt,
                      message: comment.message,
                    }}
                    createdBy={{
                      businessUserId: comment.BusinessUserId,
                      userId: comment.BusinessUser.User.id,
                      userName: comment.BusinessUser.User.name,
                      businessId: comment.BusinessUser.Business.id,
                      businessName: comment.BusinessUser.Business.name,
                      userProfileImageUrl:
                        comment.BusinessUser.User.profileImageUrl,
                      jobTitle: comment.BusinessUser.jobTitle,
                    }}
                    lineClamp={props.clamp}
                  />
                );
              }
            })}
          </CardContent>
        )}
      </Card>
      <CommentDialog
        orderId={props.orderId}
        active={createCommentActive}
        onDismiss={() => setCreateCommentActive(false)}
        mutation={mutation}
        type={"comment"}
      />
    </>
  );
};
